import React, { useEffect, useState } from 'react';
import { fmtDate, toInputDate } from '../utils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { Badge, ActivityFeed, Money, TaskList, Modal } from '../components/ui';
import { ContractForm } from './Contracts';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['draft','sent','under_review','active','expired','cancelled'];
const STATUS_COLORS = { draft:'#94a3b8',sent:'#3b82f6',under_review:'#f59e0b',active:'#16a34a',expired:'#dc2626',cancelled:'#64748b' };
const MOD_STATUSES = ['ATP','FE'];

function ModForm({ contractId, mod, onSave, onClose }) {
  const [form, setForm] = useState({ mod_number:'',mod_title:'',mod_status:'ATP',effective_date:'',pop_start_date:'',pop_end_date:'',funded_amount:'',mod_summary:'', ...mod });
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.mod_number) return setError('Mod number required');
    setSaving(true);
    try {
      const r = mod?.id
        ? await api.put(`/contracts/${contractId}/mods/${mod.id}`, form)
        : await api.post(`/contracts/${contractId}/mods`, form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={mod?.id ? 'Edit Mod' : 'Add Contract Mod'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Mod Number *</label><input className="form-input" value={form.mod_number} onChange={set('mod_number')} placeholder="MOD-001" /></div>
          <div className="form-group"><label className="form-label">Mod Status</label>
            <select className="form-select" value={form.mod_status} onChange={set('mod_status')}>{MOD_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">Mod Title</label><input className="form-input" value={form.mod_title} onChange={set('mod_title')} /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Effective Date</label><input className="form-input" type="date" value={toInputDate(form.effective_date)} onChange={set('effective_date')} /></div>
          <div className="form-group"><label className="form-label">Funded Amount ($)</label><input className="form-input" type="number" value={form.funded_amount} onChange={set('funded_amount')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">POP Start Date</label><input className="form-input" type="date" value={toInputDate(form.pop_start_date)} onChange={set('pop_start_date')} /></div>
          <div className="form-group"><label className="form-label">POP End Date</label><input className="form-input" type="date" value={toInputDate(form.pop_end_date)} onChange={set('pop_end_date')} /></div>
        </div>
        <div className="form-group"><label className="form-label">Mod Summary</label><textarea className="form-textarea" value={form.mod_summary} onChange={set('mod_summary')} rows={3} /></div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Mod'}</button>
      </div>
    </Modal>
  );
}

export default function ContractDetail() {
  const { id } = useParams(); const navigate = useNavigate(); const { can } = useAuth();
  const [contract, setContract] = useState(null); const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); const [showEdit, setShowEdit] = useState(false);
  const [showModForm, setShowModForm] = useState(false); const [editMod, setEditMod] = useState(null);
  const [users, setUsers] = useState([]);

  const load = () => api.get(`/contracts/${id}`).then(r => setContract(r.data)).catch(() => navigate('/contracts')).finally(() => setLoading(false));
  useEffect(() => { load(); api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {}); }, [id]);

  const handleStatusChange = async (status) => { await api.put(`/contracts/${id}`, { status }); load(); };

  if (loading) return <div style={{ padding:40,color:'var(--gray-400)' }}>Loading...</div>;
  if (!contract) return null;

  const daysRemaining = contract.end_date ? Math.ceil((new Date(String(contract.end_date).split('T')[0])-new Date(new Date().toISOString().split('T')[0]))/(1000*60*60*24)) : null;
  const expiringSoon = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 30;
  const totalModFunding = (contract.mods||[]).reduce((s,m) => s+(m.funded_amount||0), 0);

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/contracts" className="detail-back">← Contracts</Link>
          <div style={{ display:'flex',alignItems:'center',gap:12 }}>
            <span className="page-title">{contract.name}</span>
            <Badge status={contract.status} />
            <Badge status={contract.role} />
          </div>
          <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:2,fontFamily:'monospace' }}>
            {contract.contract_number}{contract.po_number ? ` · PO: ${contract.po_number}` : ''}
          </div>
        </div>
        {can('edit') && <div style={{ display:'flex',gap:8 }}>
          <button className="btn btn-secondary" onClick={() => { setEditMod(null); setShowModForm(true); }}>+ Add Mod</button>
          <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>✏️ Edit</button>
        </div>}
      </div>
      <div className="page-body">
        {expiringSoon && (
          <div className="archive-banner" style={{ background:'#fff7ed',borderColor:'#fed7aa',color:'#9a3412' }}>
            ⚠️ This contract expires in <strong>{daysRemaining} days</strong> ({fmtDate(contract.end_date)}).  Time to renew or extend.
          </div>
        )}



        {/* Tabs */}
        <div style={{ display:'flex',gap:2,marginBottom:20,borderBottom:'1px solid var(--gray-200)' }}>
          {['overview','mods','tasks','activity'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding:'8px 16px',background:'none',border:'none',borderBottom:tab===t?'2px solid var(--primary)':'2px solid transparent',color:tab===t?'var(--primary)':'var(--gray-500)',fontWeight:tab===t?600:400,fontSize:13,cursor:'pointer',textTransform:'capitalize',marginBottom:-1 }}>
              {t}{t==='mods'&&contract.mods?.length>0?` (${contract.mods.length})`:''}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="detail-grid">
            <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
              {/* KPI row */}
              <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12 }}>
                {[
                  { label:'Total Value', value:<Money value={contract.value} />, color:'var(--success)' },
                  { label:'Funded Value', value:<Money value={contract.funded_value} />, color:'var(--primary)' },
                  { label:'Days Remaining', value: daysRemaining !== null ? (daysRemaining < 0 ? 'Expired' : `${daysRemaining}d`) : '—', color: expiringSoon?'var(--warning)':daysRemaining<0?'var(--danger)':'var(--gray-700)' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="card" style={{ padding:'14px 16px',textAlign:'center' }}>
                    <div style={{ fontSize:11,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:18,fontWeight:700,color }}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header"><span className="card-title">Contract Details</span></div>
                <div className="card-body">
                  <div className="info-grid">
                    {[
                      ['Procurement Type', contract.procurement_type],
                      ['Contract Type', contract.contract_type],
                      ['Role', <Badge status={contract.role} />],
                      contract.role === 'Sub' && ['Prime Name', contract.prime_name],
                      ['Contract Vehicle', contract.contract_vehicle],
                      ['Security Clearance', contract.security_clearance],
                      ['Base Year TCV', <Money value={contract.base_year_tcv} />],
                      ['Option Years', contract.option_year_count ?? '—'],
                      ['Est. Gross Margin', contract.estimated_gross_margin_pct ? `${contract.estimated_gross_margin_pct}%` : '—'],
                      ['Subcontract #', contract.subcontract_number],
                      ['Account', contract.account_name ? <span className="td-link" style={{ cursor:'pointer' }} onClick={() => navigate(`/accounts/${contract.account_id}`)}>{contract.account_name}</span> : null],
                      ['Start Date', fmtDate(contract.start_date)],
                      ['End Date', fmtDate(contract.end_date)],
                      ['Owner', contract.owner_name],
                    ].filter(Boolean).map(([label, val]) => (
                      <div key={label}><div className="detail-label">{label}</div><div className="detail-value">{val||'—'}</div></div>
                    ))}
                  </div>
                  {contract.description && <><hr className="divider" /><div className="detail-label">Description</div><div className="detail-value" style={{ marginTop:4,whiteSpace:'pre-wrap' }}>{contract.description}</div></>}
                </div>
              </div>

              {/* Mod funding summary if mods exist */}
              {contract.mods?.length > 0 && (
                <div className="card" style={{ padding:'14px 20px' }}>
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                    <div><div style={{ fontSize:12,color:'var(--gray-500)',marginBottom:2 }}>Total Mod Funding</div><div style={{ fontSize:20,fontWeight:700,color:'var(--primary)' }}><Money value={totalModFunding} /></div></div>
                    <div><div style={{ fontSize:12,color:'var(--gray-500)',marginBottom:2 }}>Mods Filed</div><div style={{ fontSize:20,fontWeight:700 }}>{contract.mods.length}</div></div>
                  </div>
                </div>
              )}
            </div>

            <div className="card">
              <ActivityFeed relatedType="contract" relatedId={id} activities={contract.activities||[]} canContribute={can('contribute')} onAdd={load} />
            </div>
          </div>
        )}

        {tab === 'mods' && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Contract Modifications ({contract.mods?.length||0})</span>
              {can('edit') && <button className="btn btn-primary btn-sm" onClick={() => { setEditMod(null); setShowModForm(true); }}>+ Add Mod</button>}
            </div>
            {contract.mods?.length === 0
              ? <div className="card-body"><div className="empty-state"><div className="empty-state-icon">📎</div><div className="empty-state-text">No mods yet</div></div></div>
              : <div className="table-container mod-table">
                  <table>
                    <thead><tr><th>Mod #</th><th>Title</th><th>Status</th><th>Effective Date</th><th>POP Start</th><th>POP End</th><th>Funded Amount</th><th></th></tr></thead>
                    <tbody>
                      {contract.mods.map(m => (
                        <tr key={m.id}>
                          <td style={{ fontFamily:'monospace',fontWeight:600 }}>{m.mod_number}</td>
                          <td>{m.mod_title||'—'}</td>
                          <td><span style={{ background:m.mod_status==='ATP'?'#dcfce7':'#dbeafe',color:m.mod_status==='ATP'?'#15803d':'#1d4ed8',padding:'2px 8px',borderRadius:12,fontSize:11,fontWeight:600 }}>{m.mod_status}</span></td>
                          <td>{fmtDate(m.effective_date)}</td>
                          <td>{fmtDate(m.pop_start_date)}</td>
                          <td>{fmtDate(m.pop_end_date)}</td>
                          <td><Money value={m.funded_amount} /></td>
                          <td>
                            {can('edit') && <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditMod(m); setShowModForm(true); }}>✏️</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contract.mods.some(m => m.mod_summary) && (
                    <div style={{ padding:'16px 20px',borderTop:'1px solid var(--gray-100)' }}>
                      {contract.mods.filter(m => m.mod_summary).map(m => (
                        <div key={m.id} style={{ marginBottom:12 }}>
                          <div style={{ fontSize:12,fontWeight:600,color:'var(--gray-700)',marginBottom:4 }}>{m.mod_number} — Summary</div>
                          <div style={{ fontSize:13,color:'var(--gray-600)',whiteSpace:'pre-wrap' }}>{m.mod_summary}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            }
          </div>
        )}

        {tab === 'tasks' && (
          <div style={{ maxWidth:640 }}>
            <div className="card">
              <TaskList relatedType="contract" relatedId={id} tasks={contract.tasks||[]} users={users} canContribute={can('contribute')} onUpdate={load} />
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div style={{ maxWidth:720 }}>
            <div className="card">
              <ActivityFeed relatedType="contract" relatedId={id} activities={contract.activities||[]} canContribute={can('contribute')} onAdd={load} />
            </div>
          </div>
        )}
      </div>

      {showEdit && <ContractForm contract={contract} onSave={() => { setShowEdit(false); load(); }} onClose={() => setShowEdit(false)} />}
      {showModForm && <ModForm contractId={id} mod={editMod} onSave={() => { setShowModForm(false); load(); }} onClose={() => setShowModForm(false)} />}
    </>
  );
}
